import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePremium } from '../hooks';

interface PremiumRouteProps {
  children: React.ReactNode;
}

/**
 * PremiumRoute - Route guard that redirects free users to home and shows the paywall.
 *
 * Wrap premium-only route elements with this component. If the user is not premium,
 * they are redirected to `/` and the paywall modal is triggered.
 *
 * @example
 * <PremiumRoute>
 *   <Suspense fallback={<PageLoader />}>
 *     <WorldAroundMeSelector />
 *   </Suspense>
 * </PremiumRoute>
 */
export const PremiumRoute: React.FC<PremiumRouteProps> = ({ children }) => {
  const { isPremium, isLoading, showPaywall } = usePremium();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isPremium) {
      showPaywall();
      navigate('/', { replace: true });
    }
  }, [isPremium, isLoading, showPaywall, navigate]);

  // Still loading premium status — render nothing until resolved
  if (isLoading) {
    return null;
  }

  // Not premium — redirect effect will fire, render nothing meanwhile
  if (!isPremium) {
    return null;
  }

  return <>{children}</>;
};
